import React, { useMemo, useState, useEffect } from 'react';
import { Row, Col, Button, Tabs, Steps, Image, Divider } from 'antd';
import { UserAddOutlined, SolutionOutlined, SettingOutlined, TrophyOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";

import { api } from 'api';
import { Stat, FccSection } from 'components';
import navigationConsts from 'constants/navigationConsts';
import { useChangePageTitle } from 'utils/useChangePageTitle';

import TeamStandings from './partials/TeamStandings';
import PlayerStandings from './partials/PlayerStandings';

import useUser from "hooks/useUser";
import useScreenSize from "hooks/useScreenSize";
import useAdmin from "hooks/useAdmin";

const HomePage = () => {

    const [{ isTabletOrMobile }] = useScreenSize();

    const [homepageStats, setHomepageStats] = useState([]);
    const [heroLinks, setHeroLinks] = useState([]);
    const [loading, setLoading] = useState(true);

    const { adminData } = useAdmin();
    const navigate = useNavigate();
    const changePageTitle = useChangePageTitle();

    const { userData, loggedOut } = useUser();

    const getHomePageStats = useMemo(() => async () => {
        try {
            const results = await api.admin.getHomepageStats();
            setHomepageStats(results.data);
            setLoading(false);
        } catch (error) {
            console.error("ERROR_511");
        }
    });

    const getHeroLinks = useMemo(() => () => {
        setHeroLinks([
            {
                link: 'new-user',
                navItem: navigationConsts.CREATE_ACCOUNT,
                title: 'Sign Up',
                description: 'Register an account to participate.',
                icon: ! loggedOut ? null : <UserAddOutlined />,
                disabled: ! loggedOut,
            },
            {
                link: 'create-team',
                title: 'Create Team',
                description: 'Enter your team in the fantasy league.',
                icon: (! loggedOut && userData?.teamId) ? null : <SolutionOutlined />,
                disabled: (! loggedOut && userData?.teamId),
                navItem: navigationConsts.CREATE_TEAM,
            },
            {
                link: (! loggedOut && userData?.teamId) ? `team/${userData.teamId}` : 'create-team',
                title: 'Manage',
                description: 'Make 1 free weekly transfer.',
                icon: <SettingOutlined />,
                navItem: (! loggedOut && userData?.teamId) ? navigationConsts.MY_TEAM : navigationConsts.CREATE_TEAM,
            },
            {
                link: 'team-standings',
                title: 'Follow',
                description: 'Watch your team fly up the table!',
                icon: <TrophyOutlined />,
                navItem: navigationConsts.TEAM_STANDINGS
            }
        ]);
    });

    useEffect(() => {
        getHomePageStats();
        changePageTitle('Home');
        getHeroLinks();
    }, [userData]);

    const clickHero = (current) => {
        navigate(heroLinks[current]['link']);
    }

    return (
        <>
            <FccSection>
                <Steps 
                    onChange={clickHero} 
                    current={5}
                    className="site-navigation-steps"
                    items={heroLinks.map(hero => {
                        return {
                            key: hero.link,
                            title: hero.title,
                            icon: hero.icon || null,
                            description: hero.description,
                            disabled: hero.disabled
                        }
                    })}
                    />
            </FccSection>

            <Divider />
            
            <FccSection>    
                <Row align="middle" gutter={{ md: 24}}>
                    {adminData.getLogo() && (
                        <Col xs={24} md={5} style={{ marginBottom: isTabletOrMobile ? 20 : 0, textAlign: isTabletOrMobile ? 'center' : 'left' }} >
                            <Image 
                                src={adminData.getLogo()} 
                                style={{ maxWidth: 240 }} 
                                fallback="data:image/png;base64,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" 
                                />
                        </Col>
                    )}
                    <Col xs={24} md={adminData.getLogo() ? 19 : 24}>
                        <div dangerouslySetInnerHTML={{__html: adminData.getHomePageText()}}></div>
                    </Col>
                    
                </Row>
            </FccSection>

            <FccSection>
                <Tabs 
                    defaultActiveKey="1"
                    items={[
                        {
                            label: 'Team Standings',
                            key: 'team-standings',
                            children: <>
                                <TeamStandings />
                                <Row justify="end" style={{ marginTop: 8 }}>
                                    <Col>
                                        <Button 
                                            type="text" 
                                            size="small"
                                            onClick={() => navigate('team-standings')}
                                            >
                                            View All
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        },
                        {
                            label: 'Player Standings',
                            key: 'player-standings',
                            children: <>
                                <PlayerStandings />
                                <Row justify="end" style={{ marginTop: 8 }}>
                                    <Col>
                                        <Button 
                                            type="text" 
                                            size="small"
                                            onClick={() => navigate('player-standings')}
                                            >
                                            View All
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        }
                    ]}
                    />
            </FccSection>

            <FccSection>
                <Divider>Game Week {adminData.getGameweek()} Stats</Divider>
                <Row type="flex">
                    <Col xs={24} md={12} xl={6}>
                        <Stat
                            type="blank" 
                            title="AVERAGE SCORE" 
                            value={homepageStats.averageWeeklyScore} 
                            loading={loading} 
                            />
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                        <Stat 
                            type="blank" 
                            title="HIGHEST SCORE" 
                            value={homepageStats.topScoringTeam ? homepageStats.topScoringTeam.weekPoints : 0} 
                            loading={loading}
                            link={
                                <Button 
                                    type="link" 
                                    onClick={() => navigate(`team/${homepageStats.topScoringTeam.id}`)}
                                    >
                                        {homepageStats.topScoringTeam && homepageStats.topScoringTeam.manager.name}
                                </Button>
                            }
                            />
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                        <Stat 
                            type="blank" 
                            title="TEAMS ENTERED" 
                            value={homepageStats.numberOfTeams} 
                            loading={loading} 
                            />
                    </Col>
                    <Col xs={24} md={12} xl={6}>
                        <Stat 
                            type="blank" 
                            title="GAMEWEEK TRANSFERS" 
                            value={homepageStats.weeklyTotalTransfers} 
                            loading={loading} 
                            />
                    </Col>
                </Row>
            </FccSection>    
        </>
    )
}

export default HomePage;